import { computed, onBeforeUnmount, ref } from 'vue';
import _ from 'lodash';

// Long enough that typing a name or routing number does not fire a check per keystroke.
// Kept in step with the Portal's usePossibleBrokerageWarning composable, which uses the
// same value under the same name.
export const BROKERAGE_CHECK_DEBOUNCE_MS = 500;

export type BrokerageDetectionResult = { possibleBrokerage: boolean; primaryBrokerage: string };

export type PreviewStatus = 'idle' | 'pending' | 'success' | 'error';

export interface UsePossibleBrokerageWarningOptions {
    /** Getter for the beneficiary bank routing number. */
    getRoutingNumber: () => string;
    /** Getter for the account holder name; may be blank. */
    getAccountName: () => string;
    /** The preview endpoint variant to call; a null/undefined result is treated as a failure. */
    previewApi: (routingNumber: string, accountName: string) => Promise<BrokerageDetectionResult | null>;
    /** Routing-number validator, injected so the composable stays free of the bank-lookup module. */
    isRoutingNumberValid: (routingNumber: string) => boolean;
    /** Getter gating the check to the relevant flow (e.g. a resolved LaunchDarkly company flag). */
    isEnabled: () => boolean;
    /** Called on a positive check so the view can reveal the Final Credit To fields. */
    onPossibleBrokerage?: () => void;
}

/**
 * Detection engine extracted from CollectAccountDetails.vue. Watches a wire destination while a
 * user fills it in and flags it as a possible brokerage account. The routing number and account
 * name, the preview API, the routing-number validator, and the feature gate are all injected, so
 * the engine holds no view internals and touches no view-owned state (the collapse it once opened
 * is now the view's job via `onPossibleBrokerage`).
 *
 * Fail fast: while the check is active, a pending or failed preview blocks submission and a failure
 * is surfaced — the destination could not be verified, so it is NOT silently treated as "no
 * brokerage". `previewStatus` drives that gate; consumers read `isPreviewPending` / `previewFailed`
 * / `preventSubmit`. When the check is inactive (flag off, or the routing number is not usable) the
 * status stays 'idle', so preventSubmit is false and nothing is gated — behaviour is unchanged when
 * the feature is off.
 */
export function usePossibleBrokerageWarning({
    getRoutingNumber,
    getAccountName,
    previewApi,
    isRoutingNumberValid,
    isEnabled,
    onPossibleBrokerage,
}: UsePossibleBrokerageWarningOptions) {
    const possibleBrokerage = ref(false);
    // Name the preview attributes the destination to; rendered in the warning heading.
    const primaryBrokerage = ref('');

    // Fail-fast gate: while a check is in flight — including its debounce window — or after one
    // fails, the destination is unverified, so continuing is blocked. Stays 'idle' whenever the
    // check is inactive (invalid/blank routing), which is exactly the feature-off case, so
    // preventSubmit is false and the gate is inert. 'success' restores today's advisory-only
    // behaviour.
    const previewStatus = ref<PreviewStatus>('idle');
    const isPreviewPending = computed(() => previewStatus.value === 'pending');
    const previewFailed = computed(() => previewStatus.value === 'error');
    const preventSubmit = computed(() => isPreviewPending.value || previewFailed.value);


    // Guards against a slow response landing after a newer edit superseded it, or after the client
    // left the step — either would flicker in a stale answer.
    let latestBrokerageCheck = 0;

    const checkPossibleBrokerage = async () => {
        const routingNumber = getRoutingNumber();
        if (!isEnabled() || !isRoutingNumberValid(routingNumber)) {
            // Check inactive (flag off or invalid routing) — clear everything and
            // leave the gate inert.
            possibleBrokerage.value = false;
            primaryBrokerage.value = '';
            previewStatus.value = 'idle';
            return;
        }
        const checkId = ++latestBrokerageCheck;
        try {
            const result = await previewApi(routingNumber, getAccountName());
            if (checkId !== latestBrokerageCheck) return;
            // A null/non-success result is as much a failure as a throw — the
            // destination is unverified either way, so route both to the catch.
            if (!result) throw new Error('Brokerage destination check returned no result');
            possibleBrokerage.value = result.possibleBrokerage;
            primaryBrokerage.value = result.possibleBrokerage ? result.primaryBrokerage : '';
            previewStatus.value = 'success';
            // Hand the view the positive result so it can reveal the fields the warning points at.
            // Still only a suggestion — nothing forces the client to fill it in.
            if (result.possibleBrokerage) onPossibleBrokerage?.();
        } catch {
            // A stale check that fails after a newer edit must not clobber the
            // newer state, so honour the same supersede guard as the success path.
            if (checkId !== latestBrokerageCheck) return;
            // Fail fast: the destination couldn't be verified, so block continuing
            // and surface a visible, blocking error.
            possibleBrokerage.value = false;
            primaryBrokerage.value = '';
            previewStatus.value = 'error';
        }
    };

    const checkPossibleBrokerageDebounced = _.debounce(checkPossibleBrokerage, BROKERAGE_CHECK_DEBOUNCE_MS);

    // Start (or restart) a check. Invalidating the counter here, not just when the debounced check
    // runs, stales any answer for what the client typed a moment ago before it can land during the
    // debounce window. A new check supersedes any prior pending/error state: go straight to
    // 'pending' when active (flag on and valid routing) so the gate blocks through the debounce
    // window, else 'idle' keeps it inert.
    const kickBrokerageCheck = () => {
        latestBrokerageCheck++;
        previewStatus.value = isEnabled() && isRoutingNumberValid(getRoutingNumber())
            ? 'pending'
            : 'idle';
        checkPossibleBrokerageDebounced();
    };

    onBeforeUnmount(() => {
        // Same sequence guard as the watch: stale every in-flight check so none can
        // touch a torn-down form.
        latestBrokerageCheck++;
        checkPossibleBrokerageDebounced.cancel();
    });

    return {
        possibleBrokerage,
        primaryBrokerage,
        previewStatus,
        isPreviewPending,
        previewFailed,
        preventSubmit,
        kickBrokerageCheck,
    };
}

export default usePossibleBrokerageWarning;
